import type {
  CSSProperties,
  KeyboardEvent,
  ReactElement,
  ReactNode,
} from "react"
import { useContext, useMemo } from "react"
import { Link as GatsbyLink } from "gatsby"
import type { SectionOrgProps } from "@onestore/hel/dist/components/organisms/SectionOrg/SectionOrg.props"
import OnColorContext from "@onestore/hel/dist/contexts/OnColorContext"
import SectionSpacingValueSpaceContext from "@onestore/hel/dist/contexts/SectionSpacingValueSpaceContext"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import type Theme from "@onestore/hel/dist/typings/theme"
import type { ResponsiveBackgroundImage } from "@onestore/hel/dist/typings/types"
import {
  BACKGROUND_IMAGE_CLASS,
  BREAKPOINTS,
  type Breakpoint,
  type EdgeKeys,
  MIN_HEIGHT_CLASS,
  PADDING_CLASS,
} from "@gatsby-plugin-generic-page/components/BaseSection/classNames"
import {
  FULL_SCREEN_CONTAINER_PADDING_UNIT_LARGE,
  FULL_SCREEN_CONTAINER_PADDING_UNIT_SMALL,
  PAGE_HEADER_HEIGHT_UNIT,
} from "@gatsby-plugin-generic-page/components/BaseSection/pageHeaderOffsets"
import type { SectionSettings } from "@gatsby-plugin-generic-page/fragments/sectionSettings"
import getBackground from "@gatsby-plugin-generic-page/helpers/getBackground"
import getHasZindex from "@gatsby-plugin-generic-page/helpers/getHasZindex"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import getResponsiveBackgroundImage from "@gatsby-plugin-generic-page/helpers/getResponsiveBackgroundImage"
import getSectionOnColor from "@gatsby-plugin-generic-page/helpers/getSectionOnColor"
import getSectionPadding from "@gatsby-plugin-generic-page/helpers/getSectionPadding"
import getSectionSurfaceClass from "@gatsby-plugin-generic-page/helpers/getSectionSurfaceClass"
import { getCustomColor } from "@gatsby-plugin-generic-page/helpers/sectionColor"
import type { SpacingBase } from "@gatsby-plugin-generic-page/helpers/spacing"
import { SPACING_SPACE } from "@gatsby-plugin-generic-page/helpers/spacing"
import { unit } from "@gatsby-plugin-generic-page/helpers/unit"
import HasFullScreenContainerContext from "~/context/HasFullScreenContainerContext"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

export type BaseSectionProps = SectionSettings &
  Pick<
    SectionOrgProps,
    "role" | "hasOverflowHidden" | "viewportHeightOffsetUnit"
  > & {
    children: ReactNode
    /**
     * Overrides the background the content reads its text color from, for
     * sections whose content never sits on the section surface itself.
     */
    onColor?: Theme.Color
  }

function getEdgeKeys(
  spacing: NonNullable<SectionOrgProps["spacing"]>
): Record<Breakpoint, EdgeKeys> {
  if (typeof spacing === "string") {
    const keys: EdgeKeys = { top: spacing, bottom: spacing }

    return { small: keys, medium: keys, large: keys }
  }

  if ("top" in spacing) {
    const keys: EdgeKeys = { top: spacing.top, bottom: spacing.bottom }

    return { small: keys, medium: keys, large: keys }
  }

  return spacing
}

// `SPACING_SPACE` values are HEL space units (1 unit = 8px). Most keys hold a
// per-breakpoint object, but `sticky` is a bare `0`.
function getSpacingUnits(key: SpacingBase, breakpoint: Breakpoint): number {
  const value = SPACING_SPACE[key]

  return typeof value === "number" ? value : value[breakpoint]
}

function getImageLayer(
  image: { src: string; retinaSrc?: string } | undefined
): string | undefined {
  if (!image) {
    return undefined
  }

  // HEL swaps in `retinaSrc` through a hand-rolled device-pixel-ratio media
  // query. The prefixed `-webkit-image-set()` is the equivalent that fits in
  // a single `background-image` declaration; unprefixed `image-set()`
  // is deliberately NOT used, because an unsupported function invalidates the
  // whole `background-image` declaration — which would drop the base image, not
  // just the retina upgrade. The prefixed form is understood by Chrome 21+,
  // Safari 6+ and Firefox 90+ (and is still aliased in current versions of all
  // three), so it covers the whole browserslist range.
  return image.retinaSrc && image.retinaSrc !== image.src
    ? `-webkit-image-set(url("${image.src}") 1x, url("${image.retinaSrc}") 2x)`
    : `url("${image.src}")`
}

export default function BaseSection({
  customColor,
  id,
  padding,
  backgroundImage,
  children,
  containerSettings,
  zIndex,
  isFullViewportHeight,
  viewportHeightOffsetUnit,
  isContentVerticalCenter,
  link,
  role,
  hasOverflowHidden,
  onColor,
}: BaseSectionProps): ReactElement<BaseSectionProps> {
  const { currentPriceType } = usePriceTypeContext()
  const isSmallOnly = useBreakpoint("small", "medium")
  const isMediumOnly = useBreakpoint("medium", "large")
  const hasFullScreenContainerInFrame = useContext(
    HasFullScreenContainerContext
  )

  const edgeKeys = getEdgeKeys(getSectionPadding(padding) ?? "default")

  const customBackground = getBackground(customColor).customColor?.background
  const udsBackgroundColorClass = getSectionSurfaceClass({ customColor })
  const customBackgroundColor =
    typeof customBackground === "string"
      ? (getCustomColor(customBackground) ?? undefined)
      : undefined
  const customBackgroundGradient =
    customBackground && typeof customBackground !== "string"
      ? (getCustomColor(customBackground) ?? undefined)
      : undefined

  const responsiveBackgroundImage: ResponsiveBackgroundImage | undefined =
    isEmpty(backgroundImage)
      ? undefined
      : getResponsiveBackgroundImage(
          backgroundImage[0].images,
          backgroundImage[0].position,
          backgroundImage[0].isCover
        )

  const offsetUnit: SectionOrgProps["viewportHeightOffsetUnit"] =
    hasFullScreenContainerInFrame
      ? {
          small:
            PAGE_HEADER_HEIGHT_UNIT.small +
            2 * FULL_SCREEN_CONTAINER_PADDING_UNIT_SMALL,
          medium:
            PAGE_HEADER_HEIGHT_UNIT.medium +
            2 * FULL_SCREEN_CONTAINER_PADDING_UNIT_LARGE,
          large:
            PAGE_HEADER_HEIGHT_UNIT.large +
            2 * FULL_SCREEN_CONTAINER_PADDING_UNIT_LARGE,
        }
      : (viewportHeightOffsetUnit ?? PAGE_HEADER_HEIGHT_UNIT)

  const getOffset = (breakpoint: Breakpoint): number =>
    typeof offsetUnit === "number" ? offsetUnit : offsetUnit[breakpoint]

  const currentBreakpoint: Breakpoint = isSmallOnly
    ? "small"
    : isMediumOnly
      ? "medium"
      : "large"

  const spacingTop = getSpacingUnits(
    edgeKeys[currentBreakpoint].top,
    currentBreakpoint
  )
  const spacingBottom = getSpacingUnits(
    edgeKeys[currentBreakpoint].bottom,
    currentBreakpoint
  )

  // Memoised on the resolved numbers, not on `edgeKeys` — that object is rebuilt
  // every render, so depending on it would hand consumers a new context value
  // each time and re-render every HEL component reading this context.
  const spacingValueSpace = useMemo(
    () => ({ top: spacingTop, bottom: spacingBottom }),
    [spacingTop, spacingBottom]
  )

  const style: CSSProperties & Record<string, string | number | undefined> = {
    zIndex: zIndex ?? (getHasZindex(containerSettings) ? 2 : undefined),
  }

  style.backgroundColor = customBackgroundColor
  style.backgroundPosition = responsiveBackgroundImage?.position
  style.backgroundSize = responsiveBackgroundImage?.isCover
    ? "cover"
    : undefined

  if (!isContentVerticalCenter) {
    for (const breakpoint of BREAKPOINTS) {
      const suffix = breakpoint === "small" ? "" : `-${breakpoint}`

      style[`--section-padding-top${suffix}`] = unit(
        getSpacingUnits(edgeKeys[breakpoint].top, breakpoint) * 8
      )
      style[`--section-padding-bottom${suffix}`] = unit(
        getSpacingUnits(edgeKeys[breakpoint].bottom, breakpoint) * 8
      )
    }
  }

  if (isFullViewportHeight) {
    for (const breakpoint of BREAKPOINTS) {
      const suffix = breakpoint === "small" ? "" : `-${breakpoint}`

      style[`--section-vh-offset${suffix}`] = unit(getOffset(breakpoint))
    }
  }

  // The image lies on top of the section surface, as it did on the HEL color.
  if (responsiveBackgroundImage) {
    const { large, medium, small } = responsiveBackgroundImage

    style["--section-bg-image"] = getImageLayer(small ?? large)
    style["--section-bg-image-medium"] = getImageLayer(medium ?? large)
    style["--section-bg-image-large"] = getImageLayer(large)
  } else {
    style.backgroundImage = customBackgroundGradient ?? undefined
  }

  const linkProps = !isEmpty(link)
    ? getLinkProps(link, currentPriceType)
    : undefined

  const handleLinkKeyDown = (event: KeyboardEvent<HTMLAnchorElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      event.currentTarget.click()
    }
  }

  return (
    <OnColorContext.Provider
      value={onColor ?? getSectionOnColor({ customColor })}
    >
      <SectionSpacingValueSpaceContext.Provider value={spacingValueSpace}>
        <section
          id={id}
          role={role}
          style={style}
          className={cn(
            "relative bg-no-repeat",
            !isContentVerticalCenter && PADDING_CLASS,
            isContentVerticalCenter && "flex items-center [&>div]:flex-1",
            isFullViewportHeight && MIN_HEIGHT_CLASS,
            responsiveBackgroundImage ? BACKGROUND_IMAGE_CLASS : undefined,
            udsBackgroundColorClass,
            hasOverflowHidden && "overflow-hidden"
          )}
        >
          {children}

          {linkProps?.to ? (
            <GatsbyLink
              to={linkProps.to}
              title={linkProps.title}
              onClick={linkProps.onClick}
              onKeyDown={handleLinkKeyDown}
              className="absolute inset-0 overflow-hidden indent-[101%] whitespace-nowrap"
              data-section-link
            >
              {linkProps.text}
            </GatsbyLink>
          ) : null}
          {linkProps && !linkProps.to ? (
            <a
              href={linkProps.href}
              title={linkProps.title}
              target={linkProps.openInNewTab ? "_blank" : undefined}
              onClick={linkProps.onClick}
              onKeyDown={handleLinkKeyDown}
              className="absolute inset-0 overflow-hidden indent-[101%] whitespace-nowrap"
              data-section-link
            >
              {linkProps.text}
            </a>
          ) : null}
        </section>
      </SectionSpacingValueSpaceContext.Provider>
    </OnColorContext.Provider>
  )
}
